import { useQuery } from '@tanstack/react-query'
import { Input, Select, SpinnerWithBackground, Text } from '@viapeople/components'
import { useMemo } from 'react'
import { useParams } from 'react-router-dom'
import { trpc } from '../../../utils/trpc'
import type { CrmProvider } from '../types'

export interface ConnectionSettingsData {
	name: string
	provider: CrmProvider
	connectionId?: string
}

interface ConnectionSettingsStepProps {
	data: ConnectionSettingsData
	onChange: (data: ConnectionSettingsData) => void
	isEditMode?: boolean
}

export function ConnectionSettingsStep({ data, onChange, isEditMode = false }: ConnectionSettingsStepProps) {
	const { organizationId } = useParams<{ organizationId: string }>()

	const { data: connections, isLoading } = useQuery({
		...trpc.admin.integrations.crmConnection.getByOrganization.queryOptions({
			organizationId: organizationId as string,
		}),
		enabled: !!organizationId,
	})

	const availableConnections = useMemo(() => {
		if (!connections) return []
		return connections.filter((c) => c.provider === data.provider && c.status === 'connected')
	}, [connections, data.provider])

	const handleProviderChange = (provider: CrmProvider) => {
		onChange({ ...data, provider, connectionId: undefined })
	}

	if (isLoading) {
		return (
			<div className='py-12'>
				<SpinnerWithBackground backgroundClass='opacity-100' />
			</div>
		)
	}

	const selectedConnection = connections?.find((c) => c.id === data.connectionId)
	const noConnectionsAvailable = availableConnections.length === 0

	return (
		<div className='flex flex-col gap-6'>
			<div>
				<Text variant='medium' weight='semibold' className='mb-1'>
					Connection Settings
				</Text>
				<Text variant='small' className='text-neutral-600'>
					select a connected CRM to configure the integration.
				</Text>
			</div>

			{/* Provider Selection */}
			<div className='flex flex-col gap-4'>
				<Select
					label='CRM Provider'
					value={data.provider}
					onChange={(value) => handleProviderChange(value as CrmProvider)}
					options={[
						{ value: 'salesforce', label: 'Salesforce' },
						{ value: 'hubspot', label: 'HubSpot (Beta)' },
						{ value: 'dealcloud', label: 'DealCloud (Beta)' },
					]}
					isRequired
					disabled={isEditMode} // Cannot change provider in edit mode
				/>

				{/* Connection Selection */}
				<div>
					<Select
						label='Connection'
						value={data.connectionId || ''}
						onChange={(value) => onChange({ ...data, connectionId: value })}
						options={[
							{ value: '', label: 'Select a connection...' },
							...availableConnections.map((c) => ({
								value: c.id,
								label: `${c.name} (${new Date(c.connectedAt || '').toLocaleDateString()})`,
							})),
						]}
						isRequired
						disabled={availableConnections.length === 0}
						hint={
							noConnectionsAvailable
								? 'No active connections found for this provider. Please add a connection in the Connections tab first.'
								: undefined
						}
					/>
					{noConnectionsAvailable && (
						<div className='mt-2 p-3 bg-amber-50 border border-amber-200 rounded-md text-amber-800 text-sm'>
							No connected {data.provider} accounts found. Please go to the <strong>Connections</strong> tab to add one.
						</div>
					)}
				</div>

				{/* Integration Name */}
				<Input
					label='Integration Name'
					value={data.name}
					onChange={(e) => onChange({ ...data, name: e.target.value })}
					placeholder='e.g., Salesforce Deal Sync'
					hint='A descriptive name to identify this integration'
					isRequired
				/>
			</div>

			{selectedConnection && (
				<div className='mt-2 p-4 bg-green-50 border border-green-200 rounded-lg flex items-center gap-3'>
					<div className='w-8 h-8 rounded-full bg-green-100 flex items-center justify-center text-green-600'>✓</div>
					<div>
						<Text variant='small' weight='semibold' className='text-green-900'>
							Connected to {selectedConnection.name}
						</Text>
						<Text variant='xsmall' className='text-green-700'>
							Provider: {selectedConnection.provider}
						</Text>
					</div>
				</div>
			)}
		</div>
	)
}
